import { ClassName } from '@boxcryptor-web/shared/class-name';
import { Menu } from '@headlessui/react';
import { FiMoreHorizontal } from '@react-icons/all-files/fi/FiMoreHorizontal';
import classnames from 'classnames';
import React from 'react';
import { overrideTailwindClasses } from 'tailwind-override';
import { focusRingClassNames, useNoFocusRingOnClick } from './general';
import { IconPseudoButton } from './IconButton';
import { ExternalLink } from './link';

export const menuItemClassNames = classnames(
  'flex px-2 py-3 w-full first:rounded-t-md last:rounded-b-md',
  'text-sm text-gray-700 bg-white'
);

export const menuItemActiveClassNames = classnames(
  'hover:bg-gray-100 hover:text-gray-900',
  'focus:outline-none focus:bg-gray-100 focus:text-gray-900'
);

const disabledClassNames = classnames('opacity-75 cursor-default');

const DefaultMenuButtonContent: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <IconPseudoButton icon={<FiMoreHorizontal />} disabled={disabled} className={classnames(disabled && 'text-gray-300')} />
);

export const DropdownMenu: React.FC<
  ClassName<{
    children?: React.ReactNode;
    menuButtonContent?: React.ReactNode;
    disabled?: boolean;
    menuClassName?: string;
    align?: 'left' | 'right';
  }>
> = ({ menuButtonContent, children, className, menuClassName, disabled, align = 'left' }) => {
  const focusOutline = useNoFocusRingOnClick();
  return (
    <Menu>
      <div>
        <Menu.Button
          disabled={disabled}
          className={classnames('flex justify-center', focusOutline ? focusRingClassNames : 'focus:outline-none', className)}
        >
          {menuButtonContent || <DefaultMenuButtonContent disabled={disabled} />}
        </Menu.Button>
        <Menu.Items
          className={classnames(
            'z-50 focus:outline-none rounded-md border border-gray-200 bg-white shadow-xl absolute',
            align === 'right' && 'right-0',
            menuClassName
          )}
        >
          {children}
        </Menu.Items>
      </div>
    </Menu>
  );
};

export const DropdownMenuItem: React.FC<
  ClassName<
    {
      children?: React.ReactNode;
      disabled?: boolean;
    } & ({ onClick: () => void } | ExternalLink)
  >
> = (props) => {
  const { disabled, children, className } = props;
  const handleOnClick = (event: React.MouseEvent, onClick: () => void) => {
    event.stopPropagation();
    onClick();
  };
  return (
    <Menu.Item disabled={disabled}>
      {({ active }) =>
        'onClick' in props ? (
          <button
            onClick={(event) => handleOnClick(event, props.onClick)}
            disabled={disabled}
            className={overrideTailwindClasses(
              classnames(
                'w-56',
                menuItemClassNames,
                !disabled && menuItemActiveClassNames,
                active && !disabled && 'bg-gray-100 text-gray-900',
                disabled && disabledClassNames,
                className
              )
            )}
          >
            {children}
          </button>
        ) : (
          <ExternalLink
            {...props}
            className={overrideTailwindClasses(
              classnames(
                'w-56',
                menuItemClassNames,
                !disabled && menuItemActiveClassNames,
                active && !disabled && 'bg-gray-100 text-gray-900',
                disabled && disabledClassNames,
                className
              )
            )}
          >
            {children}
          </ExternalLink>
        )
      }
    </Menu.Item>
  );
};
